import React, { useLayoutEffect, useRef, useState } from 'react';

import { IconArrowsMaximize, IconArrowsMinimize } from '@tabler/icons-react';
import { useIntl } from 'react-intl';
import { IconButton, ToggleButton } from 'ui-kit';
import { ModalNotStyled } from 'ui-kit';

import { comparisonChartConfig, getArrayAverageNum } from './comparisonChart.config';
import { ComparisonChartKey } from './hooks/useComparisonChartData';
import { chartColors } from '../../../../configs/chart.config';
import { formatValue } from '../../../visualizations/chartsCommons';
import { EChartsBarChartVerticalDots } from '../../../visualizations/EChartsBarChartVerticalDots';
import { EChartsPieChart } from '../../../visualizations/EChartsPieChart';

interface ComparisonChartCardProps {
  label: ComparisonChartKey;
  labels: ComparisonChartKey[];
  chartsValues: Record<ComparisonChartKey, number[]>;
  mode?: 'card' | 'modal';
  onMinimizeClick?: () => void;
}

export const ComparisonChartCard = ({
  label,
  chartsValues,
  labels,
  mode = 'card',
  onMinimizeClick,
}: ComparisonChartCardProps) => {
  const intl = useIntl();

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState('0px');

  const config = comparisonChartConfig[label];

  const [chartType, setChartType] = useState<'Pie' | 'Bar'>(config.chart);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (mode !== 'modal' && el) {
      const resizeObserver = new ResizeObserver(() => {
        const measured = el.offsetHeight;
        setContainerHeight(`${measured - 80}px`);
      });

      resizeObserver.observe(el);

      // Initial set
      setContainerHeight(`${el.offsetHeight - 80}px`);

      return () => resizeObserver.disconnect();
    }
  }, [mode]);

  const handleToggle = (state: boolean) => {
    setChartType(state ? 'Pie' : 'Bar');
  };

  const chartData = chartsValues[label].map((value, idx) => {
    return {
      // label: `${labels[idx]} #${idx + 1}`,
      label: labels[idx],
      value: config?.valueFormater ? config?.valueFormater(value) : value,
      itemStyle: { color: chartColors.colorPalette[idx] },
    };
  });

  const centralLabel = 'avgLabel' in config ? config.avgLabel : 'AVERAGE';
  const avgBaseValue = getArrayAverageNum(chartsValues[label]);
  const avgValue = config?.valueFormater ? config?.valueFormater(avgBaseValue) : avgBaseValue;

  const avgFormatted =
    avgValue && config.valueFormatOptions
      ? formatValue(intl, avgValue, config.valueFormatOptions)
      : avgValue?.toString() || '-';

  return (
    <div
      id="comparisonCard"
      ref={containerRef}
      className={`relative flex flex-col !overflow-hidden rounded-md border border-accent-disabled bg-background-surface p-4 hover:border-accent-muted ${
        mode === 'modal' ? 'max-h-[80vh] w-[calc(100vw-40px)]' : 'w-auto overflow-hidden'
      } relative aspect-[16/10]`}
    >
      <div className={`mb-4 flex items-center justify-between ${mode === 'modal' ? 'pr-5' : ''}`}>
        <span className="font-heebo text-xl font-semibold text-gray-400">{label}</span>
        <div className="flex items-center gap-4">
          {mode !== 'modal' ? (
            <ModalNotStyled
              trigger={
                <IconButton
                  variant="icon-only"
                  icon={<IconArrowsMaximize size={16} color="#e85ee5" />}
                  className="rounded-full p-2"
                  onClick={() => {}}
                />
              }
            >
              {({ closeModal }) => (
                <ComparisonChartCard
                  label={label}
                  chartsValues={chartsValues}
                  labels={labels}
                  onMinimizeClick={closeModal}
                  mode="modal"
                />
              )}
            </ModalNotStyled>
          ) : (
            <IconButton
              variant="icon-only"
              icon={<IconArrowsMinimize size={16} color="#e85ee5" />}
              className="rounded-full p-3"
              onClick={onMinimizeClick}
            />
          )}
        </div>
      </div>
      <div className="absolute bottom-4 left-4 z-[2]">
        <ToggleButton
          initialState={chartType === 'Pie'}
          checkedLabel={config.chart}
          uncheckedLabel={config.chart === 'Bar' ? 'Pie' : 'Bar'}
          onChange={handleToggle}
        />
      </div>
      <div className="h-1 flex-1">
        {chartType === 'Pie' && (
          <EChartsPieChart
            visibleLegend={false}
            data={chartData}
            keyField="label"
            valueField="value"
            centralValue={avgFormatted}
            centralLabel={centralLabel}
            valueFormatOptions={config?.valueFormatOptions}
            height={mode === 'modal' ? `${window.innerHeight * 0.7}px` : containerHeight}
            width={'100%'}
            mode={mode}
          />
        )}

        {chartType === 'Bar' && (
          <EChartsBarChartVerticalDots
            data={chartData}
            keyField="label"
            valueField="value"
            yAxisLabel={label}
            valueFormatOptions={config?.valueFormatOptions}
            formatKey={(key) => key}
            height={mode === 'modal' ? `${window.innerHeight * 0.7}px` : containerHeight}
            width="100%"
            mode={mode}
            yAxisLabelPadding={config?.yAxisLabelPadding || undefined}
          />
        )}
      </div>
    </div>
  );
};
